// OrderingQuestion.tsx - Premium tap-to-swap Ordering Question Component
// Players select two items to swap their positions, with haptic feedback.
/* eslint-disable @typescript-eslint/no-explicit-any */


import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Animated as RNAnimated,
  PanResponder
} from 'react-native';
import ReAnimated from 'react-native-reanimated';

import { useFadeIn, useSpringPress, useCorrectPulse, useWrongShake } from '../../../design-system/animations';
import { DSText } from '../../../design-system/components/DSText';
import { useAppTheme } from '../../../design-system/ThemeProvider';
import { spacing, radius, shadows, quiz } from '../../../design-system/tokens';
import { useLanguageStore } from '../../../features/quiz-core/languageStore';
import { t } from '../../../i18n/translations';
import { PartialCreditService } from '../../../shared/services/partialCreditService';
import { IQESAnswerOption, IQESSceneBackdrop } from '../../../shared/ui/IQESComponents';
import { iqesColors, iqesTypography } from '../../../shared/ui/iqesDesignSystem';
import { inferIQESSceneKey } from '../../../shared/ui/iqesSceneSystem';
import { playIQESAudioCue, playIQESHaptic } from '../../../shared/ui/iqesSensoryFeedback';
import useReducedMotion from '../../../shared/ui/useReducedMotion';
import DevotionalExplanation from '../DevotionalExplanation';

import { OrderingQuestionProps, OrderingAnswer } from './types/QuestionFormatProps';

type OrderingItem = OrderingQuestionProps['question']['format_data']['items'][number];
const getItemPosition = (item: OrderingItem, fallbackPosition: number = 0): number => {
  if (typeof item.position === 'number') return item.position;
  if (typeof item.correct_position === 'number') return item.correct_position;
  return fallbackPosition;
};
const getItemLabel = (item: OrderingItem): string => item.content || item.text || '';

const DRAG_ROW_ESTIMATE = 86;

const orderingMotionStyles = StyleSheet.create({
  dragging: {
    zIndex: 20,
    elevation: 10,
    shadowColor: '#FBBF24',
    shadowOpacity: 0.36,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 8 },
  },
});

/* ── AnimatedOrderItem: per-item spring press wrapper ── */
interface AnimatedOrderItemProps {
  children: React.ReactNode;
  disabled: boolean;
  style?: any;
  itemId: number;
  index: number;
  reduceMotion: boolean;
  onDragStart: (itemId: number) => void;
  onDragMove: (index: number, dy: number) => void;
  onDragEnd: (itemId: number, index: number, dy: number) => void;
}
const AnimatedOrderItem: React.FC<AnimatedOrderItemProps> = React.memo(({
  children,
  disabled,
  style,
  itemId,
  index,
  reduceMotion,
  onDragStart,
  onDragMove,
  onDragEnd,
}) => {
  const { animatedStyle: springStyle, onPressIn, onPressOut } = useSpringPress(0.96);
  const translateY = useRef(new RNAnimated.Value(0)).current;
  const [isDragging, setIsDragging] = useState(false);
  const panResponder = React.useMemo(() => PanResponder.create({
    onStartShouldSetPanResponder: () => false,
    onMoveShouldSetPanResponder: (_event, gestureState) => (
      !disabled && !reduceMotion && Math.abs(gestureState.dy) > 8 && Math.abs(gestureState.dy) > Math.abs(gestureState.dx)
    ),
    onPanResponderGrant: () => {
      setIsDragging(true);
      onDragStart(itemId);
    },
    onPanResponderMove: (_event, gestureState) => {
      translateY.setValue(gestureState.dy);
      onDragMove(index, gestureState.dy);
    },
    onPanResponderRelease: (_event, gestureState) => {
      setIsDragging(false);
      RNAnimated.spring(translateY, {
        toValue: 0,
        useNativeDriver: true,
        speed: 22,
        bounciness: 5,
      }).start();
      onDragEnd(itemId, index, gestureState.dy);
    },
    onPanResponderTerminate: () => {
      setIsDragging(false);
      RNAnimated.spring(translateY, {
        toValue: 0,
        useNativeDriver: true,
        speed: 22,
        bounciness: 4,
      }).start();
      onDragEnd(itemId, index, 0);
    },
  }), [disabled, index, itemId, onDragEnd, onDragMove, onDragStart, reduceMotion, translateY]);

  return (
    <RNAnimated.View
      {...panResponder.panHandlers}
      style={[
        style,
        isDragging && orderingMotionStyles.dragging,
        !reduceMotion && { transform: [{ translateY }] },
      ]}
    >
      <ReAnimated.View style={springStyle}>
        {React.Children.map(children, (child) => React.isValidElement(child)
          ? React.cloneElement(child as React.ReactElement<any>, {
            onPressIn: () => {
              if (!disabled) onPressIn();
              (child.props as any).onPressIn?.();
            },
            onPressOut: () => {
              onPressOut();
              (child.props as any).onPressOut?.();
            },
          })
          : child)}
      </ReAnimated.View>
    </RNAnimated.View>
  );
});

const OrderingQuestion: React.FC<OrderingQuestionProps> = ({
  question,
  currentIndex,
  totalQuestions,
  onAnswer,
  currentOrder = [],
  disabled = false,
  showCorrectAnswer = false,
  highlightCorrect: _highlightCorrect = false,
  showExplanation: _showExplanation = false,
  timeSpent: _timeSpent = 0,
  isAnswered = false,
  dragStyle: _dragStyle = 'item',
  showPositionNumbers = true,
  animateReorder: _animateReorder = true,
  hapticOnDrag = true,
  orientation = 'vertical',
  itemSpacing: _itemSpacing = spacing.md,
  animationDuration: _animationDuration = 300,
  theme: _theme = 'premium',
  showProgressIndicator: _showProgressIndicator = true,
  accessibilityLabel,
  accessibilityHint,
  explanation
}) => {
  const { colors } = useAppTheme();
  const { language } = useLanguageStore();
  const reduceMotion = useReducedMotion();
  const styles = createStyles(colors);
  const isScreenshotSmokeQuestion = Number(question.id) >= 9100 && Number(question.id) < 9200;
  const formatData = question.format_data;
  const screenshotPhase = (formatData as typeof formatData & { screenshot_phase?: string })?.screenshot_phase;
  const isScreenshotDraggingPhase = isScreenshotSmokeQuestion && screenshotPhase === 'dragging';
  const screenshotInitialOrder = useMemo(
    () => isScreenshotSmokeQuestion
      ? [2, 1, 3, 4].filter((id) => formatData?.items?.some((item) => item.id === id))
      : [],
    [formatData?.items, isScreenshotSmokeQuestion]
  );
  const _progress = totalQuestions > 0 ? (currentIndex + 1) / totalQuestions : 0;
  const replaceTemplate = useCallback((template: string, values: Record<string, string | number>) => {
    return Object.entries(values).reduce(
      (output, [key, value]) => output.replace(new RegExp(`{{${key}}}`, 'g'), String(value)),
      template
    );
  }, []);

  // DS entrance animation for the items list
  const fadeInStyle = useFadeIn();

  // Early safety check for invalid formatData
  if (!formatData || !formatData.items || !Array.isArray(formatData.items) || formatData.items.length === 0) {
    console.error('[OrderingQuestion] Invalid or missing formatData.items:', formatData);
    return (
      <View style={styles.container}>
        <View style={styles.questionCard}>
          <DSText variant="title" align="center">{question.question}</DSText>
        </View>
        <View style={styles.instructionContainer}>
          <DSText variant="body" color={colors.incorrect} align="center">
            Error: Unable to load ordering question items
          </DSText>
        </View>
      </View>
    );
  }

  // Initialize order from props or shuffle items
  const [localOrder, setLocalOrder] = useState<number[]>(() => {
    if (currentOrder.length > 0) return currentOrder;
    if (screenshotInitialOrder.length > 0) return screenshotInitialOrder;
    // Safety check for formatData.items
    if (!formatData?.items || !Array.isArray(formatData.items)) {
      console.warn('[OrderingQuestion] Cannot initialize order: formatData.items is invalid');
      return [];
    }
    return [...formatData.items].sort(() => Math.random() - 0.5).map(item => item.id);
  });

  const [draggedItemId, setDraggedItemId] = useState<number | null>(null);
  const [previewTargetIndex, setPreviewTargetIndex] = useState<number | null>(null);

  const scrollViewRef = useRef<ScrollView>(null);

  // Get ordered items based on current order
  const getOrderedItems = useCallback(() => {
    // Safety check for undefined formatData.items
    if (!formatData?.items || !Array.isArray(formatData.items)) {
      console.warn('[OrderingQuestion] formatData.items is undefined or not an array');
      return [];
    }

    const result = localOrder
      .map(itemId => formatData.items.find(item => item && item.id === itemId))
      .filter(Boolean) as typeof formatData.items;

    return result;
  }, [localOrder, formatData?.items]);

  // Animation helpers - DISABLED for Android stability
  const animateItem = useCallback((
    itemId: number,
    _options: {
      dragging?: boolean;
      correct?: boolean;
      incorrect?: boolean;
      highlight?: boolean;
      reset?: boolean;
    } = {}
  ) => {
    // Animations disabled to prevent Android crashes
    return;
  }, []);

  // Update local order when props change
  useEffect(() => {
    if (isScreenshotSmokeQuestion && screenshotInitialOrder.length > 0) {
      setLocalOrder(screenshotInitialOrder);
    } else if (currentOrder.length > 0) {
      setLocalOrder(currentOrder);
    }
  }, [currentOrder, isScreenshotSmokeQuestion, screenshotInitialOrder]);

  // Animate items when showing correct answers
  useEffect(() => {
    if (showCorrectAnswer && !disabled) {
      const orderedItems = getOrderedItems();
      orderedItems.forEach((item, index) => {
        if (item && draggedItemId !== item.id) {
          const isCorrectPosition = getItemPosition(item, index + 1) === index + 1;
          animateItem(item.id, {
            correct: isCorrectPosition,
            incorrect: !isCorrectPosition
          });
        }
      });
    }
  }, [showCorrectAnswer, disabled, getOrderedItems, draggedItemId, animateItem]);

  // Handle tap-to-swap ordering.
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [recentlySwappedIds, setRecentlySwappedIds] = useState<number[]>([]);

  const getDragTargetIndex = useCallback((index: number, dy: number) => {
    const offset = Math.round(dy / DRAG_ROW_ESTIMATE);
    return Math.max(0, Math.min(localOrder.length - 1, index + offset));
  }, [localOrder.length]);

  const handleDragStart = useCallback((itemId: number) => {
    if (disabled || isAnswered || reduceMotion) return;
    setDraggedItemId(itemId);
    setPreviewTargetIndex(null);
    if (hapticOnDrag) {
      void playIQESHaptic('select', { enabled: !reduceMotion });
      void playIQESAudioCue('select', { enabled: !reduceMotion });
    }
  }, [disabled, hapticOnDrag, isAnswered, reduceMotion]);

  const handleDragMove = useCallback((index: number, dy: number) => {
    if (disabled || isAnswered || reduceMotion) return;
    setPreviewTargetIndex(getDragTargetIndex(index, dy));
  }, [disabled, getDragTargetIndex, isAnswered, reduceMotion]);

  const handleDragEnd = useCallback((itemId: number, index: number, dy: number) => {
    if (disabled || isAnswered || reduceMotion) return;

    const targetIndex = getDragTargetIndex(index, dy);
    setDraggedItemId(null);
    setPreviewTargetIndex(null);

    if (targetIndex === index || Math.abs(dy) < DRAG_ROW_ESTIMATE * 0.45) return;

    setLocalOrder((currentOrder) => {
      const fromIndex = currentOrder.indexOf(itemId);
      if (fromIndex < 0) return currentOrder;

      const nextOrder = [...currentOrder];
      const [movedItem] = nextOrder.splice(fromIndex, 1);
      nextOrder.splice(targetIndex, 0, movedItem);
      const targetItemId = currentOrder[targetIndex];
      setRecentlySwappedIds(targetItemId ? [itemId, targetItemId] : [itemId]);
      setTimeout(() => setRecentlySwappedIds([]), 650);
      return nextOrder;
    });

    if (hapticOnDrag) {
      void playIQESHaptic('swap', { enabled: !reduceMotion });
      void playIQESAudioCue('submit', { enabled: !reduceMotion });
    }
  }, [disabled, getDragTargetIndex, hapticOnDrag, isAnswered, reduceMotion]);

  const handleItemTap = useCallback((itemId: number) => {
    try {
      if (disabled || isAnswered) return;

      if (selectedItemId === null) {
        // Select item
        setSelectedItemId(itemId);
        void playIQESHaptic('select', { enabled: !reduceMotion });
        void playIQESAudioCue('select', { enabled: !reduceMotion });
        // Skip animation for now to prevent crashes
      } else if (selectedItemId === itemId) {
        // Deselect same item
        setSelectedItemId(null);
      } else {
        // Swap positions
        const newOrder = [...localOrder];
        const selectedIndex = newOrder.indexOf(selectedItemId);
        const targetIndex = newOrder.indexOf(itemId);

        if (selectedIndex !== -1 && targetIndex !== -1) {
          [newOrder[selectedIndex], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[selectedIndex]];
          setLocalOrder(newOrder);
          setRecentlySwappedIds([selectedItemId, itemId]);
          setTimeout(() => setRecentlySwappedIds([]), 650);
        }

        setSelectedItemId(null);

        if (hapticOnDrag) {
          void playIQESHaptic('swap', { enabled: !reduceMotion });
          void playIQESAudioCue('select', { enabled: !reduceMotion });
        }
      }
    } catch (error) {
      console.error('[OrderingQuestion] handleItemTap error:', error);
      setSelectedItemId(null); // Reset state on error
    }
  }, [disabled, hapticOnDrag, isAnswered, localOrder, reduceMotion, selectedItemId]);

  // Submit answer with partial credit calculation
  const handleSubmitAnswer = useCallback(() => {
    if (disabled || isAnswered) return;

    // Calculate partial credit if enabled
    let partialScore: number | undefined;
    let sequenceAccuracy: number | undefined;

    if (formatData.partial_credit) {
      const correctOrder = [...formatData.items]
        .sort((a, b) => getItemPosition(a, 0) - getItemPosition(b, 0))
        .map(item => item.id);

      const partialResult = PartialCreditService.calculateOrderingScore(
        localOrder,
        correctOrder,
        formatData.strict_ordering,
        formatData.partial_credit
      );

      partialScore = partialResult.total_score;
      sequenceAccuracy = partialResult.percentage / 100;
    }

    const orderingAnswer: OrderingAnswer = {
      format: 'ordering',
      order: localOrder,
      partial_score: partialScore,
      sequence_accuracy: sequenceAccuracy
    };

    if (hapticOnDrag) {
      void playIQESHaptic('submit', { enabled: !reduceMotion });
      void playIQESAudioCue('submit', { enabled: !reduceMotion });
    }

    onAnswer(orderingAnswer);
  }, [disabled, isAnswered, localOrder, onAnswer, formatData, hapticOnDrag, reduceMotion]);

  // Check if current order is correct
  const isOrderCorrect = useCallback(() => {
    return localOrder.every((itemId, index) => {
      const item = formatData.items.find(i => i.id === itemId);
      return item ? getItemPosition(item, index + 1) === index + 1 : false;
    });
  }, [localOrder, formatData.items]);

  // DS result-feedback animations for the items list
  const isCorrectOrder = showCorrectAnswer && isOrderCorrect();
  const isWrongOrder = showCorrectAnswer && !isOrderCorrect();
  const { animatedStyle: correctPulseStyle } = useCorrectPulse(isCorrectOrder);
  const wrongShakeStyle = useWrongShake(isWrongOrder);

  const orderedItems = getOrderedItems();
  const sceneKey = inferIQESSceneKey({
    question: question.question,
    questionType: 'ordering',
  });

  return (
    <View
      testID="ordering-screen"
      style={styles.container}
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={accessibilityHint}
    >
      {/* Question Card - matching MCQ Single design */}
      <IQESSceneBackdrop sceneKey={sceneKey} style={styles.questionCard}>
        <View style={styles.questionScrim}>
          <DSText variant="title" color={iqesColors.ivory} align="center" style={styles.questionTextExtra}>
            {question.question}
          </DSText>
          <DSText variant="label" color={iqesColors.gold} align="center" style={styles.instructionTextExtra}>
            {formatData.instruction || (t('quizFlow.orderItems', language) || 'Arrange the items in the correct order')}
          </DSText>
            <DSText variant="caption" color={iqesColors.mutedWarm} align="center" style={styles.instructionSubtext}>
            {t('quizFlow.tapToSwap', language) || 'Tap to select, then tap another to swap'}
          </DSText>
          {formatData.items.length > 6 && (
            <DSText variant="caption" color={iqesColors.mutedWarm} align="center" style={styles.uxHint}>
              {replaceTemplate(
                t('quizFlow.orderingTakeYourTime', language) || '{{count}} items - take your time',
                { count: formatData.items.length }
              )}
            </DSText>
          )}
        </View>
      </IQESSceneBackdrop>

      {/* Items List */}
      <ReAnimated.View style={[
        fadeInStyle,
        isCorrectOrder && correctPulseStyle,
        isWrongOrder && wrongShakeStyle,
      ]}>
      <ScrollView
        ref={scrollViewRef}
        style={[
          styles.itemsContainer,
          orientation === 'horizontal' && styles.horizontalContainer
        ]}
        contentContainerStyle={styles.scrollContent}
        horizontal={orientation === 'horizontal'}
        showsVerticalScrollIndicator={orientation === 'vertical'}
        showsHorizontalScrollIndicator={orientation === 'horizontal'}
        scrollEnabled
      >
        {orderedItems.filter(item => item != null && item.id != null).map((item, index) => {
          const isSelected = selectedItemId === item.id;
          const wasRecentlySwapped = recentlySwappedIds.includes(item.id);
          const isSyntheticDraggingItem = isScreenshotDraggingPhase && index === 0;
          const isDragging = draggedItemId === item.id || isSyntheticDraggingItem;
          const isDropPreview = (previewTargetIndex === index && draggedItemId !== null && !isDragging) ||
            (isScreenshotDraggingPhase && index === 1);
          const isCorrectPosition = showCorrectAnswer && getItemPosition(item, index + 1) === index + 1;
          const isWrongPosition = showCorrectAnswer && getItemPosition(item, index + 1) !== index + 1;

          const itemState: 'default' | 'selected' | 'correct' | 'incorrect' | 'disabled' = isSelected || isSyntheticDraggingItem
            ? 'selected'
            : isCorrectPosition
              ? 'correct'
              : isWrongPosition
                ? 'incorrect'
                : disabled || isAnswered
                  ? 'disabled'
                  : 'default';

          const ItemComponent = (
            <AnimatedOrderItem
              disabled={disabled || isAnswered}
              itemId={item.id}
              index={index}
              reduceMotion={reduceMotion}
              onDragStart={handleDragStart}
              onDragMove={handleDragMove}
              onDragEnd={handleDragEnd}
              style={[
                styles.orderItemWrapper,
                isSelected && styles.orderItemSelectedMotion,
                isDragging && styles.orderItemDraggingMotion,
                isDropPreview && styles.orderItemDropPreview,
                wasRecentlySwapped && styles.orderItemSwappedMotion,
              ]}
            >
              {(isSelected || isDragging) && (
                <View style={styles.orderMagnetGlow} pointerEvents="none" />
              )}
              <IQESAnswerOption
                label={getItemLabel(item)}
                index={showPositionNumbers ? index : undefined}
                badgeLabel={showPositionNumbers ? String(getItemPosition(item, index + 1)) : undefined}
                state={itemState}
                style={styles.sequenceItem}
                onPress={() => handleItemTap(item.id)}
                disabled={disabled || isAnswered}
                accessibilityLabel={`Item ${index + 1}: ${getItemLabel(item)}, position ${index + 1} of ${orderedItems.length}${isSelected ? ', selected' : ''}`}
              />
            </AnimatedOrderItem>
          );

          return (
            <React.Fragment key={item.id}>
              {ItemComponent}
            </React.Fragment>
          );
        })}
      </ScrollView>
      </ReAnimated.View>

      {/* Submit Button */}
      {!isAnswered && !isScreenshotSmokeQuestion && (
        <View style={styles.submitContainer}>
          <TouchableOpacity
            style={styles.submitButton}
            onPress={handleSubmitAnswer}
            disabled={disabled}
            accessibilityLabel={t('quizFlow.submitCurrentOrderAccessibility', language)}
            accessibilityRole="button"
            accessibilityState={{ disabled }}
          >
              <DSText variant="body" color={iqesColors.ink} style={styles.submitButtonTextExtra}>
              {t('quizFlow.submitOrder', language) || 'Submit Order'}
              </DSText>
            </TouchableOpacity>
          </View>
      )}

      {/* Order Status */}
      {showCorrectAnswer && (
        <View style={styles.statusContainer}>
          <DSText
            variant="body"
            color={isOrderCorrect() ? colors.correctDark : colors.incorrectDark}
            style={styles.statusTextExtra}
          >
            {isOrderCorrect()
              ? (t('quizFlow.perfectOrder', language) || '\u2713 Perfect Order!')
              : (t('quizFlow.orderNeedsAdjustment', language) || '\u2717 Order needs adjustment')}
          </DSText>
        </View>
      )}

      {/* Devotional Explanation */}
      {showCorrectAnswer && explanation && (
        <DevotionalExplanation explanation={String(explanation || '')} />
      )}

    </View>
  );
};

const createStyles = (colors: any) => StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },

  // Progress Section
  progressSection: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xl,
  },
  progressBadge: {
    backgroundColor: colors.primaryContainer,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radius.lg,
    marginRight: spacing.md,
    borderWidth: 1,
    borderColor: colors.outlineVariant,
  },
  progressTextExtra: {
    fontWeight: '700',
  },
  progressBarContainer: {
    flex: 1,
    height: 8,
    backgroundColor: colors.primaryContainer,
    borderRadius: radius.sm,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.outlineVariant,
  },
  progressBar: {
    height: '100%',
    backgroundColor: colors.primary,
    borderRadius: radius.sm,
  },

  // Question Card - matching MCQ Single design
  questionCard: {
    backgroundColor: 'rgba(10, 14, 32, 0.78)',
    borderRadius: radius.lg,
    marginBottom: spacing.xl,
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.36)',
    ...shadows.md,
    overflow: 'hidden',
    minHeight: 190,
  },
  questionScrim: {
    flex: 1,
    minHeight: 190,
    justifyContent: 'center',
    padding: spacing['2xl'],
    backgroundColor: 'rgba(3, 5, 16, 0.42)',
  },

  // Question Text extras
  questionTextExtra: {
    fontFamily: iqesTypography.displayFamily,
    fontWeight: '700',
    lineHeight: 30,
    marginBottom: spacing.md,
  },

  instructionTextExtra: {
    fontWeight: '600' as const,
    marginBottom: spacing.xs,
    letterSpacing: 0.3,
  },
  instructionSubtext: {
    marginBottom: spacing.xs,
  },

  // Items Container
  itemsContainer: {
    flex: 1,
    marginBottom: spacing.md,
  },
  scrollContent: {
    paddingVertical: spacing.sm,
    flexGrow: 1,
  },
  orderItemWrapper: {
    marginBottom: spacing.sm,
    position: 'relative',
  },
  sequenceItem: {
    minHeight: 62,
    borderRadius: radius.lg,
    paddingHorizontal: spacing.lg,
    backgroundColor: 'rgba(18, 13, 34, 0.76)',
    borderColor: 'rgba(251, 191, 36, 0.24)',
  },
  orderItemSelectedMotion: {
    transform: [{ translateY: -3 }],
  },
  orderItemDraggingMotion: {
    transform: [{ scale: 1.02 }],
  },
  orderItemDropPreview: {
    borderRadius: radius.xl,
    backgroundColor: 'rgba(251, 191, 36, 0.1)',
    borderWidth: 1,
    borderColor: 'rgba(253, 230, 138, 0.45)',
  },
  orderItemSwappedMotion: {
    transform: [{ translateY: -2 }],
  },
  orderMagnetGlow: {
    position: 'absolute',
    top: -5,
    bottom: -5,
    left: -5,
    right: -5,
    borderRadius: radius.xl,
    borderWidth: 1,
    borderColor: 'rgba(253, 230, 138, 0.72)',
    backgroundColor: 'rgba(251, 191, 36, 0.2)',
  },
  horizontalContainer: {
    flexDirection: 'row',
  },

  // Item Styles
  item: {
    backgroundColor: colors.surfaceContainerLow,
    borderRadius: radius.lg,
    ...shadows.sm,
    borderWidth: 2,
    borderColor: colors.outlineVariant,
  },
  premiumItem: {
    ...shadows.md,
  },
  horizontalItem: {
    minWidth: 200,
  },
  selectedItem: {
    borderColor: colors.accent,
    shadowColor: colors.accent,
    shadowOpacity: 0.25,
  },
  draggedItem: {
    borderColor: colors.primary,
    shadowColor: colors.primary,
    shadowOpacity: 0.3,
    elevation: 8,
  },
  disabledItem: {
    backgroundColor: colors.surfaceContainerHigh,
    borderColor: colors.outlineVariant,
    shadowOpacity: 0,
    elevation: 0,
  },

  // Item Content
  itemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.lg,
    minHeight: quiz.minTapTarget + 12,
  },
  itemMainContent: {
    flex: 1,
    marginLeft: spacing.md,
  },
  itemTextBase: {
    fontWeight: '600',
  },
  itemTextBold: {
    fontWeight: '700',
  },
  reviewHint: {
    alignSelf: 'flex-start',
    marginTop: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: radius.md,
    borderWidth: 1,
  },
  reviewHintCorrect: {
    backgroundColor: colors.correctContainer,
    borderColor: colors.correct,
  },
  reviewHintIncorrect: {
    backgroundColor: colors.incorrectContainer,
    borderColor: colors.incorrect,
  },
  reviewHintText: {
    fontWeight: '700',
    letterSpacing: 0.2,
  },

  // Position Indicator
  positionIndicator: {
    width: spacing['3xl'],
    height: spacing['3xl'],
    borderRadius: radius.full,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  positionNumber: {
    fontWeight: '800',
  },

  // Drag Handle
  dragHandle: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: spacing.xl,
    height: spacing.xl,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: spacing.sm,
  },
  dragHandleDot: {
    width: 3,
    height: 3,
    borderRadius: 1.5,
    backgroundColor: colors.outline,
    margin: 1,
  },

  // Submit Button
  submitContainer: {
    paddingVertical: spacing.sm,
  },
  submitButton: {
    backgroundColor: '#FDE68A',
    borderRadius: radius.full,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.xl,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.65)',
    shadowColor: '#F59E0B',
    shadowOpacity: 0.28,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 6 },
    elevation: 4,
  },
  submitButtonTextExtra: {
    fontWeight: '900',
    fontSize: 15,
    letterSpacing: 0.5,
  },

  // Status
  statusContainer: {
    marginBottom: spacing.lg,
    padding: spacing.md,
    borderRadius: radius.md,
    alignItems: 'center',
  },
  statusTextExtra: {
    fontWeight: '700',
  },

  // Partial Credit Info
  partialCreditContainer: {
    padding: spacing.md,
    backgroundColor: colors.correctContainer,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.correct,
  },
  partialCreditSubtextExtra: {
    fontStyle: 'italic',
    marginTop: spacing.xs,
  },
  scoreBreakdown: {
    marginTop: spacing.sm,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.correct,
  },
  scoreBreakdownTitleExtra: {
    marginBottom: spacing.xs,
  },
  scoreBreakdownTotalExtra: {
    marginTop: spacing.xs,
  },

  // UX Hint for long lists
  uxHint: {
    marginTop: spacing.sm,
  },
});

export default OrderingQuestion;
